import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {FormsModule, ReactiveFormsModule, FormControl} from '@angular/forms';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {BehaviorSubject, Observable} from 'rxjs';
import {TranslateModule} from '@ngx-translate/core';
import {get} from 'lodash';
import {
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  SelectModule,
  TextareaFieldComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {Q9HtmlEncoderPipe, Release} from '@q9elements/ui-kit/common';
import {RequestsService} from '../../../../../core/requests/requests.service';
import {DiagramJsonHandlerService} from '../../../../../diagram-data/diagram-json-handler.service';
import {VersionsService} from '../../../../../core/services/versions.service';
import {AuthService} from '../../../../../core/auth/auth.service';
import {BaseReadyForForm} from '../../../utils/base-ready-for-form';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {CreateReleaseDialogComponent} from '../create-release-dialog/create-release-dialog.component';

interface FormSubmitInterface {
  action: string;
  release: string;
  comment: string;
  children: boolean;
  password?: string;
}

enum MODAL_VIEW {
  VERSIONING = 0,
  RELEASE = 1
}

@Component({
  selector: 'app-make-ready-for-release-popup',
  templateUrl: './make-ready-for-release-popup.component.html',
  styleUrls: ['./make-ready-for-release-popup.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSlideToggleModule,
    TranslateModule,
    DialogCloseButtonComponent,
    TextFieldComponent,
    TextareaFieldComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    CreateReleaseDialogComponent,
    InfoMessageComponent,
    SelectModule,
    Q9HtmlEncoderPipe
  ]
})
export class MakeReadyForReleasePopupComponent extends BaseReadyForForm implements OnInit {
  public havePassword: boolean;
  public MODAL_VIEW = MODAL_VIEW;
  private currentViewSubject: BehaviorSubject<MODAL_VIEW> = new BehaviorSubject<MODAL_VIEW>(
    this.MODAL_VIEW.VERSIONING
  );
  public currentView$: Observable<MODAL_VIEW> = this.currentViewSubject.asObservable();
  public newRelease = {name: 'Create new release', createNew: true};
  public isLoading = signal({submit: false, releases: false});

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: any,
    private requestsSvc: RequestsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private dialogRef: MatDialogRef<MakeReadyForReleasePopupComponent>,
    private authService: AuthService,
    private versionsSvc: VersionsService
  ) {
    super();
  }

  ngOnInit() {
    this.fetchReleases();
    this.initializeForm(this.authService.getUserDataSync().havePassword);
  }

  get childrenControl() {
    return this.form.get('children') as FormControl;
  }

  get taskForConfirm(): RequestTask {
    const data: FormSubmitInterface = {
      action: 'override',
      children: this.form.get('children').value,
      comment: this.form.get('comment').value,
      release: this.form.get('release').value?.id,
      password: ''
    };

    if (this.havePassword) {
      data.password = this.form.get('password').value;
    }

    return {
      isApi: true,
      method: 'POST',
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/state/draft`,
      data
    };
  }

  public async onSubmit() {
    if (this.form.invalid || this.isLoading().submit) {
      return;
    }

    try {
      this.isLoading.mutate(state => (state.submit = true));

      await this.requestsSvc.addToQueue(this.taskForConfirm, true);
      await this.versionsSvc.getVersions();

      this.dialogRef.close(true);
    } catch (error) {
      const handledError = get(error, 'error', error);

      this.error = handledError?.error ?? handledError;
    } finally {
      this.isLoading.mutate(state => (state.submit = false));
    }
  }

  public releaseSelected(option: Release & typeof this.newRelease) {
    if (option?.createNew) {
      this.form.get('release').reset();
      this.switchToCreateRelease();
    }
  }

  public onCreateRelease(release: Release) {
    this.releases.push(release);
    this.form.get('release').setValue(release);
    this.switchToVersioning();
  }

  public switchToCreateRelease(): void {
    this.currentViewSubject.next(MODAL_VIEW.RELEASE);
  }

  public switchToVersioning(): void {
    this.currentViewSubject.next(MODAL_VIEW.VERSIONING);
  }

  private fetchReleases() {
    this.isLoading.mutate(state => (state.releases = true));
    this.passedData.releases$.pipe(this.takeUntilComponentDestroy()).subscribe(data => {
      this.releases = data.releases;
      this.isLoading.mutate(state => (state.releases = false));
      this.versionRelease = data.draftDiagramRelease;
    });
  }
}
